/// <reference types="google.maps" />
import * as React from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import {
  defaultOnClusterClickHandler,
  MarkerClusterer,
  MarkerUtils,
  SuperClusterAlgorithm,
  type Cluster,
  type Renderer,
} from '@googlemaps/markerclusterer';
import { OpenPopupContext } from './KoobMap';

type Register = (
  key: string,
  marker: google.maps.marker.AdvancedMarkerElement | null,
) => void;

export const ClustererContext = React.createContext<{
  register: Register;
  /** Keys of the pins that have a popup; a group pages through these. */
  popupKeys: Set<string>;
  /** Redraws the groups after a pin changes how it looks (muted). */
  refresh: () => void;
} | null>(null);

// Same 60px grouping radius as the old react-google-maps clusterer.
const CLUSTER_RADIUS_PX = 60;

// Group up to Google's deepest zoom: pins on the same spot must never stack
// unclustered, where only the top one could be clicked.
const CLUSTER_MAX_ZOOM = 22;

// Map-internal stacking (Google markers pane), not the app z-index scale.
const CLUSTER_Z_INDEX = 1000000;

const renderer: Renderer = {
  render: ({ count, position, markers }) => {
    const size = count < 10 ? 32 : count < 100 ? 38 : 44;
    const content = document.createElement('div');
    // A group of pins the agent cannot pick is greyed like its pins.
    const allMuted = markers?.every(
      (marker) => (marker as HTMLElement).dataset?.koobMuted === 'true',
    );
    content.className = allMuted
      ? 'koob-map-cluster koob-map-cluster--muted'
      : 'koob-map-cluster';
    content.style.width = `${size}px`;
    content.style.height = `${size}px`;
    content.textContent = String(count);

    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content,
      // Above every single pin, bigger groups on top.
      zIndex: CLUSTER_Z_INDEX + count,
    });
  },
};

// Zooming only helps when the pins sit on different spots and the map can
// still zoom in.
function canSplit(cluster: Cluster, map: google.maps.Map) {
  const [first, ...rest] = (cluster.markers ?? []).map((marker) =>
    MarkerUtils.getPosition(marker),
  );
  const allSameSpot = rest.every((position) => position.equals(first));
  const maxZoom = map.get('maxZoom') ?? CLUSTER_MAX_ZOOM;

  return !allSameSpot && (map.getZoom() ?? 0) < maxZoom;
}

/**
 * Groups the KoobMapMarker children that sit close together. Clicking a
 * group zooms onto it; when zooming cannot split it, the click opens one
 * popup that pages through its pins.
 */
export function KoobMapClusterer({ children }: { children: React.ReactNode }) {
  const map = useMap();
  const markerLibrary = useMapsLibrary('marker');
  const [, setOpen] = React.useContext(OpenPopupContext);
  const [markers, setMarkers] = React.useState<
    Record<string, google.maps.marker.AdvancedMarkerElement>
  >({});
  const popupKeys = React.useRef(new Set<string>()).current;
  const [version, setVersion] = React.useState(0);
  const latest = React.useRef({ markers, setOpen });
  latest.current = { markers, setOpen };

  const clusterer = React.useMemo(() => {
    if (!map || !markerLibrary) {
      return null;
    }

    return new MarkerClusterer({
      map,
      renderer,
      algorithm: new SuperClusterAlgorithm({
        radius: CLUSTER_RADIUS_PX,
        maxZoom: CLUSTER_MAX_ZOOM,
      }),
      onClusterClick: (event, cluster, clusterMap) => {
        if (canSplit(cluster, clusterMap)) {
          latest.current.setOpen(null);
          defaultOnClusterClickHandler(event, cluster, clusterMap);
          return;
        }
        const keys = Object.entries(latest.current.markers)
          .filter(
            ([key, marker]) =>
              popupKeys.has(key) && cluster.markers?.includes(marker),
          )
          .map(([key]) => key);
        if (keys.length > 0) {
          latest.current.setOpen({
            keys,
            index: 0,
            position: cluster.position.toJSON(),
          });
        }
      },
    });
  }, [map, markerLibrary, popupKeys]);

  React.useEffect(() => () => clusterer?.setMap(null), [clusterer]);

  React.useEffect(() => {
    clusterer?.clearMarkers();
    clusterer?.addMarkers(Object.values(markers));
  }, [clusterer, markers, version]);

  const register = React.useCallback<Register>((key, marker) => {
    setMarkers((current) => {
      if (marker ? current[key] === marker : !current[key]) {
        return current;
      }
      if (marker) {
        return { ...current, [key]: marker };
      }
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  const context = React.useMemo(
    () => ({
      register,
      popupKeys,
      refresh: () => setVersion((current) => current + 1),
    }),
    [register, popupKeys],
  );

  return (
    <ClustererContext.Provider value={context}>
      {children}
    </ClustererContext.Provider>
  );
}
